import type { ReactNode } from "react";
import { Button } from "./Button";
import { AccessDeniedState, EmptyState, EntitlementDeniedState, ErrorState, LoadingState } from "./states";

/**
 * ODS-UX-001 section 11. One vocabulary, one presentation, for every Studio
 * application. Applications select a state; they do not decide how it looks.
 */
export const STUDIO_STATES = [
  "loading",
  "empty",
  "error",
  "unauthorized",
  "unentitled",
  "unsupported",
  "dependency_unavailable",
  "ai_cannot_answer_safely",
  "verification_required",
  "concurrent_state_conflict",
  "refused_for_separation_of_duties",
  "success",
] as const;

export type StudioStateName = (typeof STUDIO_STATES)[number];

/**
 * The caller holds the permission and is still the wrong person for this
 * instance of the action. Distinct from unauthorized: no grant would resolve
 * it, so the copy never sends the customer to an administrator.
 */
export function SeparationOfDutiesState({
  action = "This action",
  reason,
  whoCanAct,
}: {
  action?: string;
  reason?: string;
  whoCanAct?: string;
}) {
  return (
    <div className="ods-state" role="status">
      <div className="ods-state__icon" aria-hidden="true">
        👥
      </div>
      <h3 className="ods-state__title">{action} is not yours to take</h3>
      <p style={{ maxWidth: 460, margin: 0 }}>
        {reason ??
          "You hold the permission this needs, but a second person has to be the one who takes it. No change to your access would alter that."}
      </p>
      {whoCanAct ? <p style={{ maxWidth: 460, margin: 0 }}>{whoCanAct}</p> : null}
    </div>
  );
}

export function StudioState({
  state,
  title,
  description,
  onRetry,
  product,
  whoCanAct,
  children,
}: {
  state: StudioStateName;
  title?: string;
  description?: string;
  onRetry?: () => void;
  product?: string;
  whoCanAct?: string;
  children?: ReactNode;
}) {
  switch (state) {
    case "loading":
      return <LoadingState label={description ?? title ?? "Loading…"} />;
    case "unauthorized":
      return <AccessDeniedState {...(description ? { reason: description } : {})} />;
    case "unentitled":
      return <EntitlementDeniedState {...(product ? { product } : {})} />;
    case "refused_for_separation_of_duties":
      return (
        <SeparationOfDutiesState
          {...(title ? { action: title } : {})}
          {...(description ? { reason: description } : {})}
          {...(whoCanAct ? { whoCanAct } : {})}
        />
      );
    case "error":
      return (
        <ErrorState
          title={title ?? "Something went wrong"}
          {...(description ? { description } : {})}
          {...(onRetry ? { onRetry } : {})}
        />
      );
    case "dependency_unavailable":
      return (
        <ErrorState
          title={title ?? "A service this page depends on is unavailable"}
          description={
            description ?? "Nothing has been lost. The records are intact and this page will work again once the service returns."
          }
          {...(onRetry ? { onRetry } : {})}
        />
      );
    case "concurrent_state_conflict":
      return (
        <ErrorState
          title={title ?? "This record changed while you were working"}
          description={description ?? "Someone else changed it. Reload to see the current state before acting again."}
          {...(onRetry ? { onRetry } : {})}
        />
      );
    case "ai_cannot_answer_safely":
      // A refusal is an answer, not a failure, and it never carries a value or
      // a confidence figure. ODS-UX-001 section 12.
      return (
        <div className="ods-failclosed" role="status">
          <strong>{title ?? "No answer the evidence supports"}</strong>
          {description ? <p style={{ margin: "var(--ods-space-2) 0 0", maxWidth: 460 }}>{description}</p> : null}
          {children}
        </div>
      );
    case "verification_required":
      return (
        <EmptyState
          icon="🔎"
          title={title ?? "Waiting on a person to verify this"}
          {...(description ? { description } : {})}
          action={children}
        />
      );
    case "unsupported":
      return (
        <EmptyState icon="ⓘ" title={title ?? "Not available here"} {...(description ? { description } : {})} action={children} />
      );
    case "success":
      return (
        <div className="ods-failclosed" role="status" style={{ borderStyle: "solid" }}>
          <strong>{title ?? "Done"}</strong>
          {description ? ` ${description}` : ""}
          {children}
        </div>
      );
    case "empty":
    default:
      return (
        <EmptyState title={title ?? "Nothing here yet"} {...(description ? { description } : {})} action={children} />
      );
  }
}

export function RetryAction({ onRetry }: { onRetry: () => void }) {
  return (
    <Button variant="secondary" size="sm" onClick={onRetry}>
      Try again
    </Button>
  );
}
